import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { formatApiError } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-8">
              <img
                src="/images/logo.png"
                alt="SVT & Co"
                className="w-12 h-12 object-contain"
              />
              <div>
                <h2 className="font-heading text-2xl font-semibold text-[#1B2A1B]">SVT & Co</h2>
                <p className="text-xs text-olive-500 tracking-wider uppercase">Tax Management Portal</p>
              </div>
            </div>
            <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Welcome back</h1>
            <p className="text-sm text-[#4F734F] mt-2">Sign in to access your tax management dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs tracking-wide uppercase font-semibold text-[#4F734F]">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@svtaaventures.com"
                className="input-field h-11"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs tracking-wide uppercase font-semibold text-[#4F734F]">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="input-field h-11 pr-10"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-olive-400 hover:text-olive-600"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#1B2A1B] hover:bg-[#2F472F] text-white font-medium rounded-sm"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="text-center text-xs text-olive-400 mt-8">
            Secure portal for SVT and Co authorized personnel only
          </p>
        </div>
      </div>

      {/* Right - Image */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <img
          src="/images/login-bg.png"
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#1B2A1B]/60 flex items-end p-12">
          <div>
            <h3 className="font-heading text-3xl font-semibold text-white mb-3">Streamline Your Tax Operations</h3>
            <p className="text-olive-200 text-sm leading-relaxed max-w-md">
              Manage clients, filings, appointments, and compliance all in one place.
              Built for Indian tax consultancy firms.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
